import { useMemo } from 'react';

import { usableCustomerIds } from '@features/products/counts/countFilters';
import { mapAggregateCounts } from '@features/products/counts/mapAggregateCounts';
import type { ProductCountsInput, ProductCountsResult } from '@features/products/counts/productCounts';

import { useGetMobileServiceCountRowsQuery } from './getMobileServiceCountRows.query.generated';
import { useGetMobileServiceCountsQuery } from './getMobileServiceCounts.query.generated';
import { buildMobileServiceCountsVariables } from './mobileServiceCountsVariables';

/**
 * The aggregate document gives the buckets, the rows document the per-customer breakdown
 * when asked. Skipped while there is no usable customer id.
 */
export const useMobileServiceCounts = ({ customerIds, window, breakdown = false, skip = false, pollOptions }: ProductCountsInput): ProductCountsResult => {
  const ids = usableCustomerIds(customerIds);
  const idsKey = ids.join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- the ids' content, not the array, is the input
  const variables = useMemo(() => buildMobileServiceCountsVariables({ customerIds: ids, window }), [idsKey, window?.startIso, window?.endIso]);
  const runCounts = !skip && ids.length > 0;
  const runRows = runCounts && breakdown;
  const common = { variables, fetchPolicy: 'cache-and-network' as const, ...pollOptions };

  const counts = useGetMobileServiceCountsQuery({ ...common, skip: !runCounts });
  const rows = useGetMobileServiceCountRowsQuery({ ...common, skip: !runRows });

  const mapped = useMemo(
    () => (counts.data ? mapAggregateCounts(counts.data, runRows ? rows.data : undefined, 'mobile-service', 'runs') : undefined),
    [counts.data, rows.data, runRows],
  );

  return {
    counts: mapped,
    loading: (counts.loading && !counts.data) || (runRows && rows.loading && !rows.data),
    error: counts.error ?? rows.error,
    // Only the queries that run are refetched; a skipped query's refetch would run it network-only.
    refetch: () => {
      if (runCounts) void counts.refetch();
      if (runRows) void rows.refetch();
    },
  };
};
